// src/config/websocket.ts

/**
 * Configuration settings for the WebSocket connection.
 */
import { getApiBaseUrl } from '../utils/environment';

/**
 * Normalize an explicit socket URL to the backend route shape. URL userinfo
 * and query parameters are discarded so credentials cannot be embedded in
 * configuration or logs; authentication travels in handshake/cookie transport.
 * Existing session and campaign suffixes select server routes and must remain.
 */
/** Normalize a configured socket URL and remove URL-carried credentials. */
export const normalizeWebSocketUrl = (value: string): string | undefined => {
  try {
    const url = new URL(value);
    if (url.protocol === 'http:') url.protocol = 'ws:';
    else if (url.protocol === 'https:') url.protocol = 'wss:';
    if (url.protocol !== 'ws:' && url.protocol !== 'wss:') return undefined;

    url.username = '';
    url.password = '';
    url.search = '';
    url.hash = '';
    const path = url.pathname.replace(/\/+$/, '');
    if (path.endsWith('/ws')) {
      url.pathname = `${path}/self`;
    } else if (/\/ws\/[^/]+$/.test(path) || /\/ws\/campaigns\/[^/]+$/.test(path)) {
      url.pathname = path;
    } else {
      url.pathname = `${path}/ws/self`;
    }
    return url.toString();
  } catch {
    return undefined;
  }
};

const deriveWebSocketUrl = (): string | undefined => {
  try {
    // Preserve any deployment proxy prefix supplied by the shared API base.
    const url = new URL(getApiBaseUrl());
    if (url.protocol === 'http:') url.protocol = 'ws:';
    else if (url.protocol === 'https:') url.protocol = 'wss:';
    if (url.protocol !== 'ws:' && url.protocol !== 'wss:') return undefined;
    url.username = '';
    url.password = '';
    url.search = '';
    url.hash = '';
    // getApiBaseUrl already includes /api; preserve any configured path prefix.
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/ws/self`;
    return url.toString();
  } catch {
    return undefined;
  }
};

const defaultLocalWebSocket = (): string => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'ws://localhost:8080/api/ws/self';
    }

    const isSecure = window.location.protocol === 'https:';
    const protocol = isSecure ? 'wss' : 'ws';
    return `${protocol}://${host}${window.location.port ? `:${window.location.port}` : ''}/api/ws/self`;
  }

  return 'ws://localhost:8080/api/ws/self';
};

const configuredWebSocketUrl =
  process.env.REACT_APP_WEBSOCKET_URL ||
  process.env.REACT_APP_WEBSOCKET_ENDPOINT;

const resolvedWebSocketUrl =
  (configuredWebSocketUrl && normalizeWebSocketUrl(configuredWebSocketUrl)) ||
  deriveWebSocketUrl() ||
  defaultLocalWebSocket();

export const webSocketConfig = {
  /**
   * The WebSocket endpoint URL.
   * Reads from environment variables, falls back to a default.
   * Example: ws://localhost:8080/api/ws/self or wss://yourdomain.com/api/ws/self
   */
  endpoint: resolvedWebSocketUrl,

  /**
   * Maximum number of reconnection attempts before giving up.
   */
  maxReconnectAttempts: 5,

  /**
   * Initial delay (in milliseconds) before the first reconnection attempt.
   */
  reconnectDelay: 1000,

  /**
   * Multiplier for exponential backoff strategy for reconnection delays.
   * e.g., 1.5 means delay increases by 50% each attempt (1000ms, 1500ms, 2250ms, ...)
   */
  reconnectBackoffFactor: 1.5,

  /**
   * Timeout (in milliseconds) for WebSocket connection attempts.
   * Note: Native WebSocket API doesn't have a direct connection timeout setting.
   * This might be used for custom timeout logic if needed.
   */
  connectionTimeout: 5000,

  /**
   * Interval (in milliseconds) for sending ping messages to keep the connection alive.
   * Set to 0 to disable automatic pings.
   * Note: Needs corresponding pong handling on the server and client implementation.
   */
  pingInterval: 30000, // e.g., send a ping every 30 seconds

  /**
   * Timeout (in milliseconds) waiting for a pong response after sending a ping.
   */
  pongTimeout: 5000, // e.g., expect pong within 5 seconds

  /**
   * Client identifier for the WebSocket connection.
   * Used to identify this client in handshake and diagnostics.
   */
  clientIdentifier: 'webapp',

  /**
   * Client version for the WebSocket connection.
   * Defaults to app version from environment or 'dev'.
   */
  clientVersion: process.env.REACT_APP_VERSION || 'dev',
};
